import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Paperclip,
  Upload,
  Trash2,
  Eye,
  FileText,
  Loader2,
  Tag,
  ArrowUp,
  ArrowDown,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listTemplateAnnexures,
  uploadTemplateAnnexure,
  patchTemplateAnnexure,
  deleteTemplateAnnexure,
  downloadTemplateAnnexureUrl,
  cloneTemplateAnnexure,
} from '../api';

/**
 * Designation tags an admin can attach to an annexure. Order matches the
 * backend `ANNEXURE_DESIGNATION_TAGS` constant in offer_letters.py — keep
 * the two in sync if either changes.
 */
const DESIGNATION_TAGS = [
  'Any',
  'Digital Consultant',
  'Team Lead',
  'Associate Digital Sales Consultant',
  'Intern',
  'Manager',
];

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB (matches backend MAX_ANNEXURE_BYTES)

const formatBytes = (n) => {
  if (!n) return '-';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const TemplateAnnexuresManager = ({ template, open, onOpenChange, onChanged }) => {
  const [annexures, setAnnexures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTag, setUploadTag] = useState('Any');
  const [uploadSort, setUploadSort] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [cloneTargetId, setCloneTargetId] = useState(null);  // annexure being cloned
  const [cloneTag, setCloneTag] = useState('Team Lead');
  const [cloning, setCloning] = useState(false);
  const fileInputRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!template?.id) return;
    setLoading(true);
    try {
      const res = await listTemplateAnnexures(template.id);
      setAnnexures(res.data?.annexures || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load annexures');
    } finally {
      setLoading(false);
    }
  }, [template?.id]);

  useEffect(() => {
    if (open && template?.id) {
      refresh();
      // reset upload form on each open
      setUploadFile(null);
      setUploadTag('Any');
      setUploadSort((annexures?.length || 0) * 10 + 10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template?.id]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf') {
      toast.error('Only PDF files are supported');
      e.target.value = '';
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error(`File too large — max ${MAX_BYTES / 1024 / 1024} MB`);
      e.target.value = '';
      return;
    }
    setUploadFile(f);
  };

  const handleUpload = async () => {
    if (!uploadFile || !template?.id) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('designation_tag', uploadTag);
      fd.append('sort_order', String(uploadSort));
      await uploadTemplateAnnexure(template.id, fd);
      toast.success(`Uploaded "${uploadFile.name}"`);
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await refresh();
      onChanged?.();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (annex) => {
    if (!template?.id) return;
    // Use window.confirm so we don't pull in another dialog primitive here.
    if (!window.confirm(`Delete "${annex.original_filename || annex.filename}"? This cannot be undone.`)) return;
    try {
      await deleteTemplateAnnexure(template.id, annex.id);
      toast.success('Annexure deleted');
      await refresh();
      onChanged?.();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleTagChange = async (annex, newTag) => {
    try {
      await patchTemplateAnnexure(template.id, annex.id, { designation_tag: newTag });
      await refresh();
      onChanged?.();
      toast.success('Designation updated');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update designation');
    }
  };

  const handleReorder = async (annex, direction) => {
    const newSort = (annex.sort_order ?? 0) + (direction === 'up' ? -15 : 15);
    try {
      await patchTemplateAnnexure(template.id, annex.id, { sort_order: newSort });
      await refresh();
    } catch (err) {
      console.error(err);
      toast.error('Failed to reorder');
    }
  };

  const handleClone = async (annex) => {
    if (!cloneTag || cloneTag === (annex.designation_tag || 'Any')) {
      toast.error('Pick a designation different from the current one.');
      return;
    }
    try {
      setCloning(true);
      await cloneTemplateAnnexure(template.id, annex.id, { designation_tag: cloneTag });
      toast.success(`Mapped same PDF to "${cloneTag}"`);
      setCloneTargetId(null);
      await refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Clone failed');
    } finally {
      setCloning(false);
    }
  };

  const handlePreview = (annex) => {
    // Open in new tab — backend serves with Content-Disposition: inline so
    // the browser renders the PDF.
    const url = downloadTemplateAnnexureUrl(template.id, annex.id);
    // Append the auth token via a fetch + blob so cookies/Authorization apply.
    // Simpler: open the URL with the token in query — but we use cookies +
    // axios headers, so spin a small fetch.
    fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token') || ''}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const objUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        window.open(objUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to preview annexure');
      });
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="annexures-manager-dialog"
        aria-describedby="annexures-manager-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip size={20} className="text-indigo-600" />
            Manage Annexures — {template.name}
            <Badge variant="outline" className="ml-auto" data-testid="annexure-count">
              {annexures.length} attached
            </Badge>
          </DialogTitle>
          <DialogDescription id="annexures-manager-description" className="text-sm text-slate-500">
            Upload PDF annexures that get appended to every offer letter generated from this template.
            Each annexure is tagged with a <b>designation</b> — only matching offer letters receive that annexure.
            Use <b>"Any"</b> to attach the annexure to all offer letters from this template.
          </DialogDescription>
        </DialogHeader>

        {/* Upload card */}
        <div className="border border-dashed border-indigo-300 bg-indigo-50/40 rounded-lg p-4 space-y-3" data-testid="annexure-upload-card">
          <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <Upload size={14} />
            Upload a new annexure
          </Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-1">
              <Label className="text-xs text-slate-500 mb-1 block">PDF file (max 10 MB)</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                data-testid="annexure-file-input"
              />
              {uploadFile && (
                <p className="text-xs text-slate-500 mt-1">
                  {uploadFile.name} · {formatBytes(uploadFile.size)}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Designation</Label>
              <Select value={uploadTag} onValueChange={setUploadTag}>
                <SelectTrigger data-testid="annexure-upload-tag">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DESIGNATION_TAGS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Sort order</Label>
              <Input
                type="number"
                value={uploadSort}
                onChange={(e) => setUploadSort(Number(e.target.value) || 0)}
                data-testid="annexure-upload-sort"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleUpload}
              disabled={!uploadFile || uploading}
              data-testid="annexure-upload-btn"
            >
              {uploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}
              {uploading ? 'Uploading…' : 'Upload PDF'}
            </Button>
          </div>
        </div>

        {/* Existing annexures list */}
        <div className="space-y-2 mt-4" data-testid="annexure-list">
          {loading ? (
            <div className="text-center text-sm text-slate-500 py-6">
              <Loader2 size={16} className="inline animate-spin mr-2" />
              Loading annexures…
            </div>
          ) : annexures.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-6 border border-dashed border-slate-200 rounded-lg">
              <FileText size={24} className="mx-auto mb-2 text-slate-400" />
              No annexures uploaded yet. Upload one above to get started.
            </div>
          ) : (
            annexures.map((a) => (
              <React.Fragment key={a.id}>
              <div
                className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors"
                data-testid={`annexure-row-${a.id}`}
              >
                <FileText size={20} className="text-indigo-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-slate-800 truncate">
                    {a.original_filename || a.filename}
                  </p>
                  <p className="text-xs text-slate-500 flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1">
                      <Tag size={11} /> {a.designation_tag || 'Any'}
                    </span>
                    <span>{formatBytes(a.file_size)}</span>
                    <span>uploaded {formatDate(a.uploaded_at)}</span>
                    {a.uploaded_by_name && <span>by {a.uploaded_by_name}</span>}
                  </p>
                </div>
                <Select value={a.designation_tag || 'Any'} onValueChange={(v) => handleTagChange(a, v)}>
                  <SelectTrigger className="w-48 h-8 text-xs" data-testid={`annexure-tag-${a.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DESIGNATION_TAGS.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleReorder(a, 'up')}
                    title="Move up"
                    data-testid={`annexure-up-${a.id}`}
                  >
                    <ArrowUp size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleReorder(a, 'down')}
                    title="Move down"
                    data-testid={`annexure-down-${a.id}`}
                  >
                    <ArrowDown size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handlePreview(a)}
                    title="Preview"
                    data-testid={`annexure-preview-${a.id}`}
                  >
                    <Eye size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                    onClick={() => {
                      setCloneTargetId((cur) => (cur === a.id ? null : a.id));
                      setCloneTag('Team Lead');
                    }}
                    title="Map this PDF to another designation"
                    data-testid={`annexure-clone-${a.id}`}
                  >
                    <Copy size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(a)}
                    title="Delete"
                    data-testid={`annexure-delete-${a.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              {cloneTargetId === a.id && (
                <div
                  className="ml-9 -mt-2 mb-1 flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded-md"
                  data-testid={`annexure-clone-panel-${a.id}`}
                >
                  <span className="text-xs text-indigo-700 font-medium">Also map this PDF to:</span>
                  <Select value={cloneTag} onValueChange={setCloneTag}>
                    <SelectTrigger className="w-48 h-7 text-xs bg-white" data-testid={`clone-tag-${a.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DESIGNATION_TAGS
                        .filter((t) => t !== (a.designation_tag || 'Any'))
                        .map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => handleClone(a)}
                    disabled={cloning}
                    data-testid={`clone-confirm-${a.id}`}
                  >
                    {cloning ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
                    {cloning ? 'Mapping…' : 'Map'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => setCloneTargetId(null)}
                    data-testid={`clone-cancel-${a.id}`}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </React.Fragment>
          ))
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateAnnexuresManager;
